/**
 * 브랜드별 매장 목록 페이지
 * HomeContent와 완전히 동일한 디자인
 */
import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Store } from 'lucide-react';
import { SearchBar } from '@/components/SearchBar';
import { RestaurantCard } from '@/components/RestaurantCard';
import { QrSourceBanner } from '@/components/QrSourceBanner';
import { useBrandStores } from '@/hooks/useBrandStores';
import { useLocationAnchor } from '@/hooks/useLocationAnchor';
import { isStoreOpen } from '@/lib/api/transformers';
import { getStoreOpenLabel } from '@/lib/storeOpenLabel';
import { useLanguage } from '@/contexts/AppContext';

export function BrandDetailPage() {
  const navigate = useNavigate();
  // useParams로 URL 파라미터 추출 - Routes/Route 구조에서 정상 작동
  const { orgParam, brandParam } = useParams<{ orgParam: string; brandParam: string }>();
  const { stores, organization: _organization, brand, loading, error } = useBrandStores(orgParam, brandParam);
  const { language } = useLanguage();
  const anchor = useLocationAnchor();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredStores = useMemo(() => {
    if (!searchQuery.trim()) return stores;
    const query = searchQuery.toLowerCase();
    return stores.filter(
      (store) =>
        store.name?.toLowerCase().includes(query) ||
        store.address?.toLowerCase().includes(query)
    );
  }, [stores, searchQuery]);

  const handleStoreClick = (storeId: string) => {
    navigate(`/restaurant/${storeId}`);
  };

  return (
    <div className="flex-1 pb-[calc(5rem+2vw)] p-[0px] m-[0px]">
      <SearchBar
        placeholder="Buscar sucursales..."
        value={searchQuery}
        onChange={setSearchQuery}
      />

      {/* #365: QR source 컨텍스트 배너 — qr-table 진입 시 픽업 폴백 안내 */}
      <QrSourceBanner anchor={anchor} />

      <div className="p-[min(5vw,1.25rem)]">
        <div className="flex items-center justify-between m-[0px]">
          <h2 className="text-[16px] font-medium text-foreground">
            {brand?.name || 'Cargando...'}
          </h2>
        </div>
        <p className="text-[14px] text-muted-foreground mb-[min(3vw,1rem)]">
          {filteredStores.length} sucursales
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Cargando...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">{error}</p>
          </div>
        ) : filteredStores.length === 0 ? (
          <div className="text-center py-8">
            <Store className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery
                ? 'No se encontraron sucursales'
                : 'No hay sucursales disponibles'}
            </p>
          </div>
        ) : (
          <div className="space-y-[min(2vw,0.5rem)]">
            {filteredStores.map((store, index) => {
              // Q7 박제 옵션 C — BE 응답에 closure_reason 키 존재 시에만
              // hours relation 로드된 것으로 간주 (P1-1 회귀 게이트, transformer
              // 의 hasQ7Hours 와 동일 판정). raw operating_hours 재해석 금지.
              const hasQ7Hours = 'closure_reason' in store;
              const isOpenNow = store.is_open_now ?? false;
              const q7Label = hasQ7Hours
                ? getStoreOpenLabel(
                    {
                      status: store.status,
                      isOpenNow,
                      closureReason: store.closure_reason ?? null,
                      nextAvailableAt: store.next_available_at ?? null,
                      storeTimezone: store.timezone ?? null,
                    },
                    language,
                  )
                : null;

              return (
                <div
                  key={store.id}
                  className="transform transition-all duration-500 ease-out opacity-0 translate-y-4 animate-fadeInUp"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animationFillMode: 'forwards',
                  }}
                >
                  <div className="hover:scale-[1.02] transition-transform duration-200 hover:shadow-lg rounded-2xl">
                    <RestaurantCard
                      id={String(store.id)}
                      name={store.name}
                      cuisine={store.address || ''}
                      rating={store.rating || 0}
                      deliveryTime=""
                      distance=""
                      imageUrl={store.image_url || ''}
                      isOpen={isStoreOpen(store.status, store.is_open_now)}
                      q7Label={q7Label}
                      onClick={handleStoreClick}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
